import React, { useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';
import { submitRating } from '../../../../api/sessionsApi';
import { useSession } from '../../../../context/SessionContext';
import './RatingModal.less';

const RatingModal = ({ show, onClose }) => {
    const { state } = useSession();
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        // Prevent double-submit while in-flight
        if (isSubmitting) return;

        // Clear any previous errors
        setError('');

        // Validate rating
        if (!rating || rating < 1 || rating > 5) {
            setError('Please select a rating');
            return;
        }

        // Validate sessionUid
        if (!state.sessionUid) {
            setError('Session not found. Please try again.');
            return;
        }

        setIsSubmitting(true);

        try {
            // Build payload with optional fields from session context
            const payload = {
                rating,
                comment: comment.trim() || undefined,
                email: state.receiptInfo?.email || undefined,
                fullName: state.receiptInfo?.fullName || undefined
            };

            console.log('[RATING] Submitting rating:', {
                sessionUid: state.sessionUid,
                rating,
                hasComment: !!payload.comment,
                hasEmail: !!payload.email,
                payload
            });

            // Submit rating to API
            console.log(`[RATING] DEBUG: Calling POST /api/sessions/${state.sessionUid}/rating`);
            const result = await submitRating(state.sessionUid, payload);
            console.log('[RATING] DEBUG: API response:', result);

            if (!result.ok) {
                console.error('[RATING] Submission failed:', result.message);
                setError(result.message || 'Could not submit rating. Please try again.');
                setIsSubmitting(false);
                return;
            }

            console.log('[RATING] Success');

            // Close modal silently on success
            onClose();
        } catch (error) {
            console.error('[RATING] Exception during submit:', error);
            setError('Could not submit rating. Please try again.');
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const handleStarKeyDown = (e, ratingValue) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setRating(ratingValue);
            // Clear error when user selects a rating
            setError('');
        }
    };

    // Rating labels (no emojis)
    const ratingLabels = {
        1: 'Poor',
        2: 'Fair',
        3: 'Good',
        4: 'Very Good',
        5: 'Excellent'
    };

    return (
        <Offcanvas 
            show={show} 
            placement="bottom" 
            onHide={onClose}
            className="rating-modal-offcanvas"
            aria-labelledby="rating-modal-title"
            style={{ height: 'auto', maxHeight: '85vh' }}
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title id="rating-modal-title">Rate Your Experience</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                    <p className="rating-description">
                        How would you rate your charging experience?
                    </p>

                    {/* Star Rating */}
                    <div className="star-rating">
                        {[...Array(5)].map((_, index) => {
                            const ratingValue = index + 1;
                            return (
                                <button
                                    key={index}
                                    type="button"
                                    className="star-button"
                                    onClick={() => {
                                        setRating(ratingValue);
                                        setError('');
                                    }}
                                    onMouseEnter={() => setHover(ratingValue)}
                                    onMouseLeave={() => setHover(0)}
                                    onKeyDown={(e) => handleStarKeyDown(e, ratingValue)}
                                    aria-label={`Rate ${ratingValue} star${ratingValue > 1 ? 's' : ''}`}
                                    tabIndex={0}
                                >
                                    <FaStar
                                        className="star-icon"
                                        size={40}
                                        color={ratingValue <= (hover || rating) ? "#ffc107" : "var(--gc-border-strong)"}
                                    />
                                </button>
                            );
                        })}
                    </div>

                    {/* Rating Label (no emojis) */}
                    {(hover || rating) > 0 && (
                        <div className="rating-label">
                            {ratingLabels[hover || rating]}
                        </div>
                    )}

                    {/* Optional Comment Field - Enter inserts newline, does not submit */}
                    <div className="comment-section">
                        <textarea
                            className="form-control comment-textarea"
                            placeholder="Additional feedback (optional)"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={3}
                            maxLength={500}
                            disabled={isSubmitting}
                            enterKeyHint="enter"
                        />
                    </div>

                    {/* Session Note (instead of showing email) */}
                    <p className="session-note">
                        We'll associate this feedback with your session.
                    </p>

                    {/* Error Message */}
                    {error && (
                        <div className="rating-error">
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="rating-actions">
                        <button
                            type="submit"
                            className="btn btn-dark btn-submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Rating'}
                        </button>

                        <button
                            type="button"
                            className="btn btn-link btn-cancel"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default RatingModal;
